import {
  FormControlLabel,
  Switch as MuiSwitch,
  type SwitchProps as MuiSwitchProps,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { SxProps } from '@mui/system';
import { type FC, type ReactNode, useMemo } from 'react';

export type SwitchProps = MuiSwitchProps & {
  label?: ReactNode;
};

export const Switch: FC<SwitchProps> = ({ label, sx, ...props }) => {
  const theme = useTheme();

  const switchStyles: SxProps = useMemo(
    () => ({
      width: 44,
      height: 24,
      padding: 0,
      '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 0.25,
        transitionDuration: '300ms',
        '&.Mui-checked': {
          color: theme.palette.common.white,
          '& + .MuiSwitch-track': {
            backgroundColor: theme.palette.SlabGray.Stroke,
            opacity: 1,
            border: 0,
          },
          '& .MuiSwitch-thumb': {
            backgroundColor: theme.palette.SlabIndigo[700],
          },
        },
        '&.Mui-disabled': {
          '& + .MuiSwitch-track': {
            opacity: 1,
            backgroundColor: theme.palette.SlabTurquoise[200],
          },
          '& .MuiSwitch-thumb': {
            opacity: 1,
            backgroundColor: theme.palette.SlabGray[400],
          },
        },
        '&.Mui-checked.Mui-disabled': {
          '& + .MuiSwitch-track': {
            opacity: 1,
            backgroundColor: theme.palette.SlabGray.Stroke,
          },
          '& .MuiSwitch-thumb': {
            opacity: 1,
            backgroundColor: theme.palette.SlabGray[500],
          },
        },
        '& .MuiSwitch-input': {
          left: 'unset',
          width: 1,
        },
      },
      '& .MuiSwitch-thumb': {
        width: 20,
        height: 20,
        backgroundColor: theme.palette.common.white,
        transition: theme.transitions.create(['background-color'], {
          duration: 500,
        }),
      },
      '& .MuiSwitch-track': {
        borderRadius: 10,
        backgroundColor: theme.palette.SlabGray[400],
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
          duration: 500,
        }),
      },
    }),
    [theme],
  );

  return (
    <FormControlLabel
      sx={{ m: 0, gap: 1, ...sx }}
      control={
        <MuiSwitch
          focusVisibleClassName='.Mui-focusVisible'
          disableRipple
          sx={switchStyles}
          {...props}
        />
      }
      label={label}
    />
  );
};
